import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { MdTextDataSchema, MdTextResponse, MdTextUISchema } from './types'
import { useCallback, useMemo } from 'react'
import { ActionsMenu } from './ActionsMenu'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { buildMdDocumentModel } from './mdDocumentModel'
import { MdTextLauncher } from './MdTextLauncher'
import { EmptyState } from '@/components/ui/EmptyState'
import { useNavigate, useLocation } from 'react-router'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'
import { buildMdTextViewerPath, type MdTextViewerLocationState } from './mdTextViewerNavigation'
import { MdTextInlineContent } from './MdTextInlineContent'

export function MdTextContent({
  uiSchema,
  dataSchema,
  tileFilters,
  reloadHandler,
  contentId,
  sheetPayload,
  cachedResponse,
}: {
  uiSchema: MdTextUISchema
  dataSchema: MdTextDataSchema
  tileFilters?: React.ReactNode
  reloadHandler: () => void
  contentId?: string
  sheetPayload?: Record<string, unknown>
  cachedResponse?: MdTextResponse
}) {
  const { title = undefined, description = undefined, actions, emphasizeHeader } = uiSchema
  const { data } = dataSchema
  const hasHeader = !!title && !!tileFilters
  const navigate = useNavigate()
  const location = useLocation()
  const { appKey } = useAppContext()
  const { tileFilterSelections } = useTileFilters()
  const documentModel = useMemo(
    () => buildMdDocumentModel(data, { forceLarge: uiSchema.forceLargeDocumentMode }),
    [data, uiSchema.forceLargeDocumentMode]
  )

  const openViewerForEdit = useCallback(() => {
    if (!appKey || !contentId) {
      return
    }
    const pathname = buildMdTextViewerPath(appKey, contentId)
    const state: MdTextViewerLocationState = {
      from: `${location.pathname}${location.search}`,
      payload: sheetPayload,
      tileFilterSelections,
      startInEditMode: true,
      cachedResponse,
    }
    navigate({ pathname, search: location.search }, { state })
  }, [
    appKey,
    cachedResponse,
    contentId,
    location.pathname,
    location.search,
    navigate,
    sheetPayload,
    tileFilterSelections,
  ])

  if (documentModel.isEmpty) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState title="No content" description="This markdown document is empty." />
      </div>
    )
  }

  if (documentModel.isLarge) {
    return (
      <div className="md-text-component flex h-full flex-col">
        {hasHeader && (
          <ComponentHeader emphasize={emphasizeHeader}>
            <ComponentTitle title={title} description={description} />
            <ComponentActionsContainer className="md-text-component__actions">
              {!tileFilters ? null : tileFilters}
              <ActionsMenu
                actions={actions ?? []}
                openEditing={openViewerForEdit}
                markdown={data}
                reloadHandler={reloadHandler}
                hideInlineEdit
              />
            </ComponentActionsContainer>
          </ComponentHeader>
        )}
        <MdTextLauncher
          contentId={contentId}
          title={title}
          description={description}
          showTitleAndDescription={!hasHeader}
          documentModel={documentModel}
          sheetPayload={sheetPayload}
          cachedResponse={cachedResponse}
        />
      </div>
    )
  }

  return (
    <MdTextInlineContent
      uiSchema={uiSchema}
      data={data}
      tileFilters={tileFilters}
      reloadHandler={reloadHandler}
      documentModel={documentModel}
    />
  )
}
